import React, { Component, ErrorInfo, ReactNode } from 'react';
import { TokenExpirationHandler } from '../../utils/tokenExpirationHandler';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[AuthErrorBoundary] Caught error:', error, errorInfo);
    
    // Check if this is a token expiration error
    if (TokenExpirationHandler.isTokenExpirationError(error)) {
      console.log('[AuthErrorBoundary] Token expiration error detected, handling...');
      await TokenExpirationHandler.handleTokenExpiration(error);
      return;
    }

    // Log other errors for debugging
    console.error('[AuthErrorBoundary] Non-authentication error:', error);
  }

  render() {
    if (this.state.hasError) {
      // Check if it's a token expiration error
      if (this.state.error && TokenExpirationHandler.isTokenExpirationError(this.state.error)) {
        // Don't render anything as we're redirecting
        return null;
      }

      // For other errors, show a generic error message
      return (
        <div style={{ 
          padding: '20px', 
          textAlign: 'center',
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
          borderRadius: '8px',
          margin: '20px'
        }}>
          <h2>Something went wrong</h2>
          <p>An unexpected error occurred. Please refresh the page and try again.</p>
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
} 